/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { usePlanwerte, useTagebuchEintraege } from '@/domain/controlling/hooks';
import { useReducer } from 'react';
import { useRouteParams } from '../useRouteParams';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { PlanwerteEditForm, PlanwerteFormReducerAction } from './types';
import {
  getDefaultAbsatzminuten,
  getDefaultAbsatzPlanwert,
  getDefaultAbsatzProduktivitaet,
  getDefaultAbsatzPrognose,
  getDefaultUmsatzPlanwert,
  getDefaultUmsatzProduktivitaet,
  getDefaultUmsatzPrognose,
  getDefaultUmsatzZielminuten,
} from '../helpers';
import { useStandortContext } from '@/features/standortContext/StandortContext';

export function usePlanwerteFormReducer() {
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { searchParams, routeParams } = useRouteParams();
  const { planwerte } = usePlanwerte({
    standortId,
    jahrMonat: {
      jahr: routeParams.jahr,
      monat: routeParams.monat,
    },
  });
  const { tagebuchEinstellungen } = useTagebuchEinstellungen(standortId);
  const { tagebuchEintraege } = useTagebuchEintraege({
    standortId,
    jahrMonat: {
      jahr: routeParams.jahr,
      monat: routeParams.monat,
    },
  });
  const currentPlanwert = planwerte.find(
    (absatzPlanwert) => absatzPlanwert.datum === searchParams.datum,
  );

  const tagebuchEintragByDatum = tagebuchEintraege.find(
    (tagebuchEintrag) => tagebuchEintrag.datum === searchParams.datum,
  );

  const initalState: PlanwerteEditForm = {
    formValues: {
      absatzPlan: getDefaultAbsatzPlanwert(currentPlanwert.absatzPlan),
      absatzProduktivitaet: getDefaultAbsatzProduktivitaet(
        tagebuchEinstellungen.absatzProduktivitaet,
        currentPlanwert.absatzPlan,
      ),
      absatzPrognose: getDefaultAbsatzPrognose(currentPlanwert.absatzPlan),
      absatzZielminuten: getDefaultAbsatzminuten(currentPlanwert.absatzPlan),
      umsatzPlan: getDefaultUmsatzPlanwert(currentPlanwert.umsatzPlan),
      umsatzProduktivitaet: getDefaultUmsatzProduktivitaet(
        tagebuchEinstellungen.umsatzProduktivitaet,
        currentPlanwert.umsatzPlan,
      ),
      umsatzPrognose: getDefaultUmsatzPrognose(currentPlanwert.umsatzPlan),
      umsatzZielminuten: getDefaultUmsatzZielminuten(
        currentPlanwert.umsatzPlan,
      ),
      interneNotiz: tagebuchEintragByDatum.interneNotiz,
    },
    hasChanges: false,
    isSubmitting: false,
    isDirty: false,
  };

  function planwerteReducer(
    state: PlanwerteEditForm,
    action: PlanwerteFormReducerAction,
  ): PlanwerteEditForm {
    switch (action.type) {
      case 'updateAbsatzPlan': {
        return {
          formValues: {
            ...state.formValues,
            absatzPlan: action.values.absatzPlan,
            absatzZielminuten: action.values.absatzZielminuten
              ? action.values.absatzZielminuten
              : state.formValues.absatzZielminuten,
          },
          hasChanges:
            action.values.absatzPlan !== initalState.formValues.absatzPlan,
          isSubmitting: state.isSubmitting,
          isDirty: true,
        };
      }
      case 'updateUmsatzPlan': {
        return {
          formValues: {
            ...state.formValues,
            umsatzPlan: action.values.umsatzPlan,
            umsatzZielminuten: action.values.umsatzZielminuten
              ? action.values.umsatzZielminuten
              : state.formValues.umsatzZielminuten,
          },
          hasChanges:
            action.values.umsatzPlan !== initalState.formValues.umsatzPlan,
          isSubmitting: state.isSubmitting,
          isDirty: true,
        };
      }
      case 'updateAbsatzProduktivitaet': {
        return {
          formValues: {
            ...state.formValues,
            absatzProduktivitaet: action.values.absatzProduktivitaet,
            absatzZielminuten: action.values.absatzZielminuten
              ? action.values.absatzZielminuten
              : state.formValues.absatzZielminuten,
          },
          hasChanges:
            action.values.absatzProduktivitaet !==
            initalState.formValues.absatzProduktivitaet,
          isSubmitting: state.isSubmitting,
          isDirty: true,
        };
      }
      case 'updateUmsatzProduktivitaet': {
        return {
          formValues: {
            ...state.formValues,
            umsatzProduktivitaet: action.values.umsatzProduktivitaet,
            umsatzZielminuten: action.values.umsatzZielminuten
              ? action.values.umsatzZielminuten
              : state.formValues.umsatzZielminuten,
          },
          hasChanges:
            action.values.umsatzProduktivitaet !==
            initalState.formValues.umsatzProduktivitaet,
          isSubmitting: state.isSubmitting,
          isDirty: true,
        };
      }
      case 'submit': {
        return {
          ...state,
          isSubmitting: true,
        };
      }
      case 'updateSubmit': {
        return {
          ...state,
          isSubmitting: action.isSubmitting,
        };
      }
      case 'updateDialogMessage': {
        return {
          ...state,
          isSubmitting: false,
          dialogMessage: action.dialogMessage,
        };
      }
      case 'updateInterneNotiz': {
        return {
          ...state,
          formValues: {
            ...state.formValues,
            interneNotiz: action.values.interneNotiz,
          },
          hasChanges:
            state.formValues.interneNotiz !== action.values.interneNotiz,
          isDirty: true,
        };
      }
      case 'reset': {
        return initalState;
      }
    }
  }

  return useReducer(planwerteReducer, initalState);
}
